import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { StatusChip, Chip } from "@/components/StatusChip";
import { PersonAvatar } from "@/components/PersonAvatar";
import { ViolationTooltip } from "@/components/expenses/ViolationTooltip";
import { FileImage, FileClock } from "lucide-react";
import { fmtUSD, fmtDate, POLICY_STATUS_META } from "@/lib/api";

// Renders one table cell for the given column key.
export const ExpenseCell = ({ col, e, onReceipt }) => {
  switch (col) {
    case "merchant":
      return <div className="text-[13px] font-medium text-white">{e.merchant}</div>;
    case "employee":
      return (
        <div className="flex items-center gap-2">
          <PersonAvatar name={e.employee_name} size={20} />
          <span className="text-[12.5px] text-[#c9c9c9]">{e.employee_name}</span>
        </div>
      );
    case "department":
      return <span className="text-[12px] text-[#A3A3A3]">{e.department}</span>;
    case "date":
      return <span className="text-[12px] tabular-nums text-[#A3A3A3]">{fmtDate(e.date)}</span>;
    case "category":
      return <span className="text-[12px] text-[#A3A3A3]">{e.category}</span>;
    case "policy":
      return (
        <span className="block max-w-[170px] truncate text-[12px] text-[#A3A3A3]" data-testid={`expenses-row-policy-${e.id}`}>
          {e.policy_name || "—"}
        </span>
      );
    case "policy_status":
      return (
        <ViolationTooltip exp={e}>
          <span data-testid={`expenses-row-policy-status-${e.id}`}>
            <Chip tone={(POLICY_STATUS_META[e.policy_status] || { tone: "neutral" }).tone}>
              {(POLICY_STATUS_META[e.policy_status] || { label: e.policy_status }).label}
            </Chip>
          </span>
        </ViolationTooltip>
      );
    case "receipt":
      if (e.has_receipt) {
        return (
          <button onClick={(ev) => { ev.stopPropagation(); onReceipt(e.id); }} data-testid={`expenses-row-receipt-${e.id}`}
            title="View receipt"
            className="flex h-7 w-7 items-center justify-center rounded-lg bg-[rgba(79,142,247,0.12)] text-[#7FAEF9] transition-colors hover:bg-[rgba(79,142,247,0.24)] hover:text-white">
            <FileImage size={13} />
          </button>
        );
      }
      if (e.status === "waiting_on_employee") {
        return (
          <Tooltip delayDuration={120}>
            <TooltipTrigger asChild>
              <span className="flex h-7 w-7 cursor-help items-center justify-center text-[#FBBF24]" data-testid={`expenses-row-receipt-waiting-${e.id}`}>
                <FileClock size={13} />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="bg-[#1c1c1c] px-3 py-1.5 text-[11.5px] text-white">
              Aria is waiting on {e.employee_name?.split(" ")[0]}'s receipt
            </TooltipContent>
          </Tooltip>
        );
      }
      return <span className="text-[12px] text-[#3a3a3a]">—</span>;
    case "assigned":
      return e.assigned_to ? (
        <div className="flex items-center gap-2" data-testid={`expenses-row-assigned-${e.id}`}>
          <PersonAvatar name={e.assigned_to.name} size={18} />
          <span className="text-[12px] text-[#c9c9c9]">{e.assigned_to.name}</span>
        </div>
      ) : (
        <span className="text-[12px] text-[#3a3a3a]" data-testid={`expenses-row-assigned-${e.id}`}>—</span>
      );
    case "amount":
      return <span className="text-[13px] font-medium tabular-nums text-white">{fmtUSD(e.amount)}</span>;
    case "status":
      return <StatusChip status={e.status} />;
    case "gl":
      return <span className="text-[12px] tabular-nums text-[#A3A3A3]">{e.gl_code}</span>;
    default:
      return null;
  }
};
